import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import i18next from 'i18next';
import { ActionCoreData, ActionType, ModelConfig } from '@bringg/types';
import { isValidPublicUrl, normalizeUrl } from '@bringg-frontend/utils';

import { ActionBaseStore, ActionsRepo, workflowsRootStore } from './internal';
import { ActionFamilyType, ClientRealtimeDriverLocationWebhook } from '../utils/types';
import { webhookModelProvider } from 'bringg-web/features/automation-workflows/actions/webhook/webhook-model-provider';

interface RealtimeDriverLocationWebhookMetadata {
	is_read_only?: boolean;
	translation_string?: string;
	action_type?: ActionType;
	base_model?: string;
}

const MIN_INTERVAL_SECONDS = 10;
const MAX_INTERVAL_SECONDS = 60;
const DEFAULT_INTERVAL_SECONDS = 30;
const DEFAULT_FIELDS = ['id', 'external_id', 'user_id', 'lat', 'lng'];

class RealtimeDriverLocationWebhookActionStore extends ActionBaseStore {
	type = ActionFamilyType.REALTIME_DRIVER_LOCATION_WEBHOOK;
	actionType: ActionType;
	intervalValue: number;
	webhookUrl: string;
	authenticationConfigurationId?: number;
	modelConfig: ModelConfig;
	globalWebhookInfraEnabled = false;

	constructor(actionToInit: ClientRealtimeDriverLocationWebhook, actionsRepo: ActionsRepo) {
		super(actionsRepo);

		makeObservable(this, {
			intervalValue: observable,
			webhookUrl: observable,
			authenticationConfigurationId: observable,
			modelConfig: observable,
			globalWebhookInfraEnabled: observable,
			actionData: override,
			title: computed,
			isValid: computed,
			mappedToServer: computed,
			setIntervalValue: action,
			setWebhookUrl: action,
			setAuthenticationConfigurationId: action,
			updateModelConfig: action,
			setGlobalWebhookInfraEnabled: action
		});

		this.actionType = actionToInit.action_type;
		this.intervalValue = actionToInit.data?.interval_value ?? DEFAULT_INTERVAL_SECONDS;
		this.webhookUrl = actionToInit.data?.webhook_url ?? '';
		this.authenticationConfigurationId = actionToInit.data?.webhook_authentication_configuration_id;

		void this.initModelConfig(actionToInit.data?.model_config);
	}

	initModelConfig = async (modelConfig: ModelConfig | undefined) => {
		if (modelConfig) {
			this.modelConfig = modelConfig;
			return;
		}

		try {
			await webhookModelProvider.loadAll();
			runInAction(() => {
				const baseModel = this.actionData?.base_model || 'task';
				const fullConfig = webhookModelProvider.getModelConfigByModel(baseModel);
				this.modelConfig = fullConfig
					? { ...fullConfig, fields: [...new Set([...DEFAULT_FIELDS, ...(fullConfig.fields || [])])] }
					: { name: baseModel, fields: DEFAULT_FIELDS };
			});
		} catch (e) {
			console.error('Failed to load webhook model config', e);
		}
	};

	// @ts-expect-error - RealtimeDriverLocationWebhookMetadata is not part of the base action metadata union
	get actionData(): RealtimeDriverLocationWebhookMetadata {
		const { metadataRepo } = workflowsRootStore.getStore();
		const meta = metadataRepo.metadata.getActionMetaData(this.type);
		return {
			...(meta as RealtimeDriverLocationWebhookMetadata),
			base_model: (meta as RealtimeDriverLocationWebhookMetadata)?.base_model || 'task'
		};
	}

	get mappedToServer(): ActionCoreData {
		const data: Record<string, unknown> = {
			interval_value: this.intervalValue,
			interval_unit: 'seconds',
			webhook_url: normalizeUrl(this.webhookUrl)
		};

		if (this.modelConfig) {
			data.model_config = this.modelConfig;
		}

		if (this.authenticationConfigurationId) {
			data.webhook_authentication_configuration_id = this.authenticationConfigurationId;
		}

		return {
			action_type: this.actionType,
			data
		} as ActionCoreData;
	}

	get workflowId(): number | undefined {
		return this.actionsRepo.workflowPredefinedId;
	}

	setGlobalWebhookInfraEnabled = (value: boolean) => {
		this.globalWebhookInfraEnabled = value;
	};

	get isValid(): boolean {
		if (this.globalWebhookInfraEnabled) {
			// Subscription state is managed independently by WebhookSelector — the action store
			// has no visibility into which webhooks are linked, so we don't gate save on it.
			return true;
		}

		if (!isValidPublicUrl(this.webhookUrl)) {
			return false;
		}

		if (!this.modelConfig) {
			return false;
		}

		let hasSelectedFields: boolean;
		const treeDataNotSet = !webhookModelProvider.getSelectedFields().length;

		if (treeDataNotSet) {
			hasSelectedFields = !!this.getSelectedFromModelConfig(this.modelConfig, []).length;
		} else {
			hasSelectedFields = !!webhookModelProvider.getSelectedFields().length;
		}

		if (!hasSelectedFields) {
			return false;
		}

		return this.intervalValue >= MIN_INTERVAL_SECONDS && this.intervalValue <= MAX_INTERVAL_SECONDS;
	}

	getSelectedFromModelConfig = (model: ModelConfig, fields: string[]): string[] => {
		fields.push(...(model.fields || []));

		model.relations?.forEach(relation => {
			const innerFields = this.getSelectedFromModelConfig(relation, fields);
			fields.push(...innerFields);
		});

		return fields;
	};

	get title(): string {
		const titleKey = this.actionData?.translation_string;
		return titleKey ? i18next.t(titleKey) : '';
	}

	setIntervalValue = (value: number) => {
		this.intervalValue = value;
	};

	setWebhookUrl = (url: string) => {
		this.webhookUrl = url;
	};

	setAuthenticationConfigurationId = (id: number | undefined) => {
		this.authenticationConfigurationId = id;
	};

	updateModelConfig = (keys: string[]) => {
		const config = webhookModelProvider.buildModelConfigFromTree(this.actionData?.base_model || 'task', keys);
		this.modelConfig = { ...config, fields: [...new Set([...DEFAULT_FIELDS, ...(config.fields || [])])] };
	};
}

export default RealtimeDriverLocationWebhookActionStore;
